import * as Sentry from '@sentry/browser';

import { API_URL, APP_VERSION } from '@/constants';
import { useAuthStore } from '@/stores/AuthState';

/**
 * Sentry DSNs per environment — mirrors the mobile app (racore-app). The DSN
 * (and therefore the environment) is chosen from the API URL the web build
 * points at, so a single codebase reports to the right Sentry project:
 *   - localhost / 127.0.0.1 → no DSN (never send in local dev)
 *   - qa.racoreapp.com      → QA project
 *   - admin.racoreapp.com   → PROD project
 */
const QA_DSN =
  'https://5a065bb033e88ba119417df289c9e530@o4508891371339776.ingest.de.sentry.io/4508891373633616';
const PROD_DSN =
  'https://7c7007dd625f42a05b1a9b48bb91cebd@o4508891371339776.ingest.de.sentry.io/4508891394146384';

export function resolveEnv(apiUrl: string = API_URL): { dsn: string | null; environment: string } {
  const url = apiUrl ?? '';
  if (url.includes('localhost') || url.includes('127.0.0.1'))
    return { dsn: null, environment: 'local' };
  if (url.includes('qa.racoreapp.com')) return { dsn: QA_DSN, environment: 'qa' };
  if (url.includes('admin.racoreapp.com')) return { dsn: PROD_DSN, environment: 'production' };
  return { dsn: null, environment: 'unknown' };
}

/** Messages matching any of these substrings are dropped before being sent to Sentry. */
const SENTRY_NOISE_PATTERNS = [
  'Translation key not found',
  'Authentication session cancelled',
  'authentication was denied',
  'Request aborted',
  'canceled',
  // Security scanner probes — not a real user error.
  'security_scan_validation_probe',
];

/**
 * Sentry beforeSend filter — exported for unit testing.
 * Generic so it works with both the real Sentry.ErrorEvent (Sentry.init) and
 * plain test fixtures without requiring the full SDK type in tests.
 * Returns null to drop the event, or the event unchanged to send it.
 */
export function sentryBeforeSend<
  T extends { message?: string; exception?: { values?: Array<{ value?: string }> } },
>(event: T): T | null {
  const msg = event.message ?? event.exception?.values?.[0]?.value ?? '';
  if (SENTRY_NOISE_PATTERNS.some((p) => msg.includes(p))) return null;
  return event;
}

/** Minimal Sentry feedback event shape (the SDK's FeedbackEvent, testable without the SDK). */
export interface FeedbackEventLike {
  contexts: { feedback: Record<string, unknown> };
  tags?: Record<string, unknown>;
}

/** The auth-store fields a feedback is enriched with. */
export interface UserInfoLike {
  id: number | null;
  username?: string | null;
  email?: string | null;
  language?: string | null;
  favorite_streaming_service?: string | null;
  feature_flag_group?: { slug: string } | null;
}

/**
 * Attach who wrote a feedback so the team can reply from Sentry. Applied to
 * FEEDBACK events only (via the `beforeSendFeedback` hook): error events keep
 * the id + username user, with no email.
 */
export function enrichFeedbackEvent<T extends FeedbackEventLike>(
  event: T,
  user: UserInfoLike,
  trigger?: FeedbackTrigger,
): T {
  // A crash can happen before auth is hydrated: the trigger still applies, only
  // the identity is missing.
  const known = user.id != null;
  const feedback = event.contexts.feedback;
  if (known && user.email) feedback.contact_email = user.email;
  if (known && user.username) feedback.name = user.username;

  const context = {
    language: known ? user.language : null,
    streaming_service: known ? user.favorite_streaming_service : null,
    feature_flag_group: known ? user.feature_flag_group?.slug : null,
    feedback_trigger: trigger,
  };
  const tags = Object.fromEntries(Object.entries(context).filter(([, value]) => value != null));
  event.tags = { ...event.tags, ...tags };
  return event;
}

/** Where a bug report was sent from (Sentry tag `feedback_trigger`). */
export type FeedbackTrigger = 'profile' | 'reco' | 'crash';

/** False without a Sentry client (local build): hide the bug-report entry points. */
export function isBugReportAvailable(): boolean {
  return Boolean(Sentry.getClient());
}

/**
 * Send a bug report from the Racore bottom sheet. Resolves once Sentry accepted
 * it, rejects otherwise (offline, 30 s timeout) so the sheet can keep the text.
 * The trigger travels in the hint — `sendFeedback`'s `tags` option would set
 * scope tags that leak onto every later event.
 */
export function submitBugReport({
  message,
  trigger,
  associatedEventId,
}: {
  message: string;
  trigger: FeedbackTrigger;
  associatedEventId?: string;
}): Promise<string> {
  return Sentry.sendFeedback(
    { message, associatedEventId },
    { includeReplay: false, data: { feedbackTrigger: trigger } },
  );
}

/**
 * Initialise Sentry for the web app. No-op in local/unknown environments.
 * Call once, as early as possible (before rendering).
 */
export function initSentry(): void {
  const { dsn, environment } = resolveEnv();
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment,
    release: `racore-web@${APP_VERSION}`,
    // Errors are always captured at 100%; sample a slice of perf traces.
    tracesSampleRate: 0.05,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend: sentryBeforeSend,
  });

  // `beforeSend` never sees feedback events; this hook sees ONLY them, so the
  // email reaches Sentry on feedbacks and never on errors. Read at send time.
  Sentry.getClient()?.on('beforeSendFeedback', (event, hint) => {
    enrichFeedbackEvent(
      event as unknown as FeedbackEventLike,
      useAuthStore.getState().userInfo,
      // The SDK types this hint narrowly, but `sendFeedback` passes ours through as-is.
      (hint as { data?: { feedbackTrigger?: FeedbackTrigger } } | undefined)?.data?.feedbackTrigger,
    );
  });

  // Tag the platform so web events are distinguishable from native in Sentry.
  Sentry.setTag('platform', 'web');
}

/** Report a handled error with context (used across the app). Returns the Sentry event id. */
export function reportError(error: unknown, context?: Record<string, unknown>): string {
  return Sentry.captureException(error, context ? { extra: context } : undefined);
}

/** Attach the current user to events (id + username only — no PII). */
export function setSentryUser(user: { id: string; username?: string } | null): void {
  Sentry.setUser(user ? { id: user.id, username: user.username } : null);
}
